import { invariantResponse } from '@epic-web/invariant'
import { Prisma } from "@prisma/client"
import { json, type LoaderFunctionArgs } from '@remix-run/node'
import { Form, Link, useFetcher, useLoaderData, type MetaFunction } from '@remix-run/react'
import { useState } from 'react'
import { brandName } from '#app/components/branding.js'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { UserActionsMenu } from '#app/components/ui/user-actions-menu.js'
import { rarityColors, getPinnedRingClass, getPinnedAchievement } from '#app/utils/achievements.js'
import { checkAndAwardAchievements, getAchievementsForProfile } from '#app/utils/achievements.server.js'
import { formatGold } from '#app/utils/tycoon/format'
import { tycoonHonours } from '#app/utils/tycoon.server.ts'
import { getUserId } from '#app/utils/auth.server.ts'
import { publicProfileCommentFilter } from '#app/utils/comments.server.js'
import { prisma } from '#app/utils/db.server.ts'
import { isFollowing, getFollowerCount, getFollowingCount } from '#app/utils/follows.server.js'
import { getUserImgSrc } from '#app/utils/misc.tsx'
import { isBlocked } from '#app/utils/user-block.server.js'
import { useOptionalUser } from '#app/utils/user.ts'

const linkTypeIcons: Record<string, string> = {
	website: '🌐',
	twitch: '📺',
	youtube: '▶️',
	discord: '💬',
	twitter: '🐦',
	github: '💻',
	other: '🔗',
}

export async function loader({ params, request }: LoaderFunctionArgs) {
	const user = await prisma.user.findUnique({
		select: {
			id: true,
			name: true,
			username: true,
			status: true,
			pinnedAchievementSlug: true,
			image: { select: { id: true } },
			createdAt: true,
			description: true,
			shards: {
				select: {
					id: true,
					shard: {
						select: {
							id: true,
							name: true,
							slug: true,
							server: {
								select: {
									id: true,
									name: true,
									slug: true,
									logo: true,
								},
							},
						},
					},
				},
			},
			characters: true,
			guilds: {
				select: {
					id: true,
					role: true,
					details: true,
					yearStart: true,
					yearEnd: true,
					guild: {
						select: {
							id: true,
							name: true,
							abbreviation: true,
							shard: {
								select: {
									name: true,
									slug: true,
									server: {
										select: { name: true, slug: true },
									},
								},
							},
						},
					},
				},
				orderBy: { createdAt: 'desc' },
			},
			connections: {
				select: {
					providerName: true,
					providerId: true,
				},
			},
			customLinks: {
				select: { id: true, linkType: true, url: true, description: true },
				orderBy: { createdAt: 'asc' },
			},
		},
		where: { username: params.username },
	})

	invariantResponse(user, 'User not found', { status: 404 })

	const viewerId = await getUserId(request)

	// Hide shadowbanned/banned users from non-admins (they can still see their own profile)
	if (user.status !== 'active') {
		const isOwnProfile = viewerId === user.id
		const isAdmin = viewerId
			? await prisma.user.findFirst({
					where: { id: viewerId, roles: { some: { name: 'admin' } } },
					select: { id: true },
				})
			: null
		if (!isOwnProfile && !isAdmin) {
			throw new Response('Not Found', { status: 404 })
		}
	}

	const userIsBlocked = viewerId && viewerId !== user.id
		? await isBlocked(viewerId, user.id)
		: false

	const [followStatus, followerCount, followingCount] = await Promise.all([
		viewerId && viewerId !== user.id ? isFollowing(viewerId, user.id) : { following: false, notify: false },
		getFollowerCount(user.id),
		getFollowingCount(user.id),
	])

	const result = await prisma.$queryRaw<
		{ userNotesCount: BigInt; userScriptsCount: BigInt; userReactinsCount: BigInt; userShardsCount: BigInt; userCharactersCount: BigInt; userBuildsCount: BigInt; userCommentsCount: BigInt; userGuidesCount: BigInt; userGuildsCount: BigInt }[]
		>(
		Prisma.sql`
			SELECT
			(SELECT COUNT(*) FROM Note WHERE ownerId = ${user.id}) AS userNotesCount,
			(SELECT COUNT(*) FROM Script WHERE creatorId = ${user.id}) AS userScriptsCount,
			(SELECT COUNT(*) FROM Reaction WHERE userId = ${user.id}) AS userReactinsCount,
			(SELECT COUNT(*) FROM UserShard WHERE userId = ${user.id}) AS userShardsCount,
			(SELECT COUNT(*) FROM Character WHERE userId = ${user.id}) AS userCharactersCount,
			(SELECT COUNT(*) FROM UserBuildTemplate WHERE userId = ${user.id}) AS userBuildsCount,
			(SELECT COUNT(*) FROM Comment WHERE userId = ${user.id}) AS userCommentsCount,
			(SELECT COUNT(*) FROM Guide WHERE creatorId = ${user.id}) AS userGuidesCount,
			(SELECT COUNT(*) FROM UserGuild WHERE userId = ${user.id}) AS userGuildsCount
		`
	)

	const counts = result && result.length
	? {
		notes: Number(result[0]?.userNotesCount),
		scripts: Number(result[0]?.userScriptsCount),
		reactions: Number(result[0]?.userReactinsCount),
		shards: Number(result[0]?.userShardsCount),
		characters: Number(result[0]?.userCharactersCount),
		builds: Number(result[0]?.userBuildsCount),
		comments: Number(result[0]?.userCommentsCount),
		guides: Number(result[0]?.userGuidesCount),
		guilds: Number(result[0]?.userGuildsCount),
		}
	: { notes: 0, scripts: 0, reactions: 0, shards: 0, characters: 0, builds: 0, comments: 0, guides: 0, guilds: 0 }

	// Recent activity for sidebar
	const [recentScripts, recentBuilds, recentGuides, recentComments] = await Promise.all([
		prisma.script.findMany({
			where: { creatorId: user.id },
			select: { title: true, slug: true, updatedAt: true },
			orderBy: { updatedAt: 'desc' },
			take: 3,
		}),
		prisma.userBuildTemplate.findMany({
			where: { userId: user.id },
			select: { name: true, slug: true, modifiedAt: true, server: { select: { slug: true } } },
			orderBy: { modifiedAt: 'desc' },
			take: 3,
		}),
		prisma.guide.findMany({
			where: { creatorId: user.id },
			select: { title: true, slug: true, updatedAt: true },
			orderBy: { updatedAt: 'desc' },
			take: 3,
		}),
		prisma.comment.findMany({
			where: { userId: user.id, ...publicProfileCommentFilter },
			select: {
				id: true,
				text: true,
				createdAt: true,
				script: { select: { title: true, slug: true } },
				guide: { select: { title: true, slug: true } },
				build: { select: { name: true, slug: true, server: { select: { slug: true } } } },
				newsPost: { select: { title: true, slug: true } },
				fanSite: { select: { title: true, slug: true } },
				botScriptBuild: { select: { id: true, name: true } },
			},
			orderBy: { createdAt: 'desc' },
			take: 3,
		}),
	])

	// Filter connections
	const connections = user.connections.filter((c) => c.providerName === 'twitch')

	// Character names with counts
	const nameCounts = user.characters.reduce((acc: Record<string, number>, { name }) => {
		acc[name] = (acc[name] || 0) + 1
		return acc
	}, {})
	const characterNames = Object.entries(nameCounts).map(([name, count]) => ({ name, count }))

	// Check and award achievements (fire-and-forget on own profile)
	if (viewerId === user.id) {
		checkAndAwardAchievements(user.id).catch(console.error)
	}
	const profileAchievements = await getAchievementsForProfile(user.id)
	const tycoon = await tycoonHonours(user.id)
	const coreAchievements = profileAchievements.filter((a) => !a.bonus)
	const unlockedAchievements = coreAchievements.filter((a) => a.unlocked)

	return json({
		user,
		counts,
		connections,
		characterNames,
		recentScripts,
		recentBuilds,
		recentGuides,
		recentComments,
		userJoinedDisplay: user.createdAt.toLocaleDateString(),
		userIsBlocked,
		followStatus,
		followerCount,
		followingCount,
		achievements: profileAchievements,
		tycoon,
		unlockedCount: unlockedAchievements.length,
		rarityColors,
		pinnedRingClass: getPinnedRingClass(user.pinnedAchievementSlug),
		pinnedAchievement: getPinnedAchievement(user.pinnedAchievementSlug),
	})
}

/** Vendor Tycoon season finishes that earn a badge, by rank. */
const TYCOON_PODIUM: Record<number, { medal: string; title: string }> = {
	1: { medal: '🥇', title: 'Champion' },
	2: { medal: '🥈', title: 'Runner-Up' },
	3: { medal: '🥉', title: 'Third Place' },
}

export default function ProfileRoute() {
	const data = useLoaderData<typeof loader>()
	const { user, counts, connections, characterNames, recentScripts, recentBuilds, recentGuides, recentComments, achievements, tycoon, unlockedCount, rarityColors: colors, pinnedRingClass, pinnedAchievement } = data
	const userDisplayName = user.name ?? user.username
	const loggedInUser = useOptionalUser()
	const isLoggedInUser = user.id === loggedInUser?.id

	const [pendingExternalUrl, setPendingExternalUrl] = useState<string | null>(null)

	const statLinks = [
		{ to: 'activity', label: 'Activity', value: null, icon: '📋' },
		{ to: 'scripts', label: 'Scripts', value: counts.scripts, icon: '📜' },
		{ to: 'builds', label: 'Builds', value: counts.builds, icon: '🛡️' },
		{ to: 'guides', label: 'Guides', value: counts.guides, icon: '📖' },
		{ to: 'comments', label: 'Comments', value: counts.comments, icon: '💬' },
		{ to: 'reactions', label: 'Reactions', value: counts.reactions, icon: '⭐' },
		...(isLoggedInUser ? [{ to: 'notes', label: 'Notes', value: counts.notes, icon: '📝' }] : []),
		{ to: 'shards', label: 'Shards', value: counts.shards, icon: '🌐' },
		{ to: 'characters', label: 'Characters', value: counts.characters, icon: '⚔️' },
		{ to: 'guilds', label: 'Guilds', value: counts.guilds, icon: '🏰' },
		{ to: 'achievements', label: 'Achievements', value: unlockedCount, icon: '🏅' },
	]

	return (
		<div className="container mx-auto mb-24 overflow-x-hidden px-4 pt-8 md:mb-48">
			{/* Hero section */}
			<div className="relative">
				<div className="flex flex-col items-center">
					<img
						src={getUserImgSrc(user.image?.id)}
						alt={userDisplayName}
						className={`h-32 w-32 rounded-full border-4 border-background object-cover shadow-lg md:h-40 md:w-40 ${pinnedRingClass}`}
					/>
					{pinnedAchievement && (
						<Link
							to={`/users/${user.username}/achievements`}
							className="mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground hover:underline"
							title={pinnedAchievement.description}
						>
							<span>{pinnedAchievement.icon}</span>
							<span>{pinnedAchievement.name}</span>
						</Link>
					)}
					<h1 className={`${pinnedAchievement ? 'mt-1' : 'mt-4'} text-2xl font-bold md:text-4xl`}>{userDisplayName}</h1>
					{connections.length > 0 && (
						<div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
							<span>Verified on:</span>
							{connections.map((connection) => {
								if (connection.providerName === 'twitch') {
									return (
										<a
											key={connection.providerName}
											href={`https://twitch.tv/${connection.providerId}`}
											target="_blank"
											rel="noopener noreferrer"
											className="inline-flex items-center gap-1 hover:text-foreground"
										>
											<img src="/images/twitch.png" alt="Twitch" className="h-4 w-4" />
											<span>{connection.providerId}</span>
										</a>
									)
								}
								return null
							})}
						</div>
					)}
					<p className="mt-1 text-sm text-muted-foreground">
						Joined: {data.userJoinedDisplay}
					</p>
					<div className="mt-2 flex gap-4 text-sm text-muted-foreground">
						<span><strong className="text-foreground">{data.followerCount}</strong> {data.followerCount === 1 ? 'follower' : 'followers'}</span>
						<span><strong className="text-foreground">{data.followingCount}</strong> following</span>
					</div>
					{isLoggedInUser ? (
						<div className="mt-3 flex gap-2">
							<Link
								to="/settings/profile"
								className="rounded-full bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary hover:bg-primary/20"
							>
								Edit Profile
							</Link>
							<Form action="/logout" method="POST">
								<button
									type="submit"
									className="rounded-full bg-muted px-4 py-1.5 text-sm font-medium text-muted-foreground hover:bg-muted-foreground/10"
								>
									<Icon name="exit" className="mr-1 inline scale-110" />
									Logout
								</button>
							</Form>
						</div>
					) : loggedInUser ? (
						<div className="mt-3 flex items-center gap-2">
							<FollowButton targetUserId={user.id} initialFollowing={data.followStatus.following} initialNotify={data.followStatus.notify} />
							<Link
								to={`/messages/new?to=${user.username}`}
								className="rounded-full bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary hover:bg-primary/20"
							>
								Send Message
							</Link>
							<UserActionsMenu
								targetUserId={user.id}
								targetUsername={user.username}
								isBlocked={data.userIsBlocked}
							/>
						</div>
					) : null}
				</div>
			</div>

			{/* Stats / Navigation */}
			<div className="-mx-4 mt-6 flex gap-2 overflow-x-auto px-4 pb-2 md:mx-0 md:mt-8 md:flex-wrap md:gap-3 md:overflow-x-visible md:px-0 md:pb-0">
				{statLinks.map((item) => (
					<Link
						key={item.to}
						to={item.to}
						className="min-w-[4.5rem] shrink-0 rounded-xl bg-muted p-2.5 text-center transition-colors hover:bg-muted-foreground/10 md:min-w-[5.5rem] md:flex-1 md:p-3"
					>
						{item.value !== null ? (
							<div className="text-base font-bold md:text-2xl">{item.value}</div>
						) : (
							<div className="text-base md:text-2xl">{item.icon}</div>
						)}
						<div className="text-[0.65rem] text-muted-foreground md:text-xs">{item.label}</div>
					</Link>
				))}
			</div>

			{/* Main content area */}
			<div className="mt-6 grid gap-6 md:mt-8 lg:grid-cols-3 lg:gap-8">
				{/* Left column - profile info */}
				<div className="space-y-6 lg:col-span-2">
					{/* About */}
					<div className="rounded-xl border bg-muted/30 p-4 md:p-6">
						<h2 className="text-lg font-semibold">About</h2>
						<p className="mt-2 text-sm text-muted-foreground whitespace-pre-wrap break-words">
							{user.description || 'This user hasn\'t written anything about themselves yet.'}
						</p>
						{user.customLinks.length > 0 && (
							<div className="mt-4 flex flex-wrap gap-2">
								{user.customLinks.map((link) => (
									<button
										key={link.id}
										onClick={() => setPendingExternalUrl(link.url)}
										className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/50 px-3 py-1 text-sm transition-colors hover:bg-muted hover:text-foreground"
									>
										<span>{linkTypeIcons[link.linkType] ?? '🔗'}</span>
										<span>{link.description || link.linkType}</span>
									</button>
								))}
							</div>
						)}
					</div>

					{/* Achievements */}
					{achievements.some((a) => a.unlocked) && (
						<div className="rounded-xl border bg-muted/30 p-4 md:p-6">
							<div className="flex items-center justify-between">
								<h2 className="text-lg font-semibold">Achievements</h2>
								<span className="text-xs text-muted-foreground">{unlockedCount} / {achievements.filter((a) => !a.bonus).length}</span>
							</div>
							<div className="mt-3 flex flex-wrap gap-2">
								{achievements
									.filter((a) => a.unlocked)
									.map((a) => {
										const rarity = colors[a.rarity as keyof typeof colors] ?? colors.common
										return (
											<div
												key={a.slug}
												title={`${a.name} — ${a.description}`}
												className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${rarity.bg} ${rarity.text} ${rarity.border}`}
											>
												<span>{a.icon}</span>
												<span>{a.name}</span>
											</div>
										)
									})}
							</div>
						</div>
					)}

					{/* Vendor Tycoon: all-time gold and season finishes */}
					{tycoon && (tycoon.allTimeGold > 0 || tycoon.seasons.length > 0) && (
						<div className="rounded-xl border bg-muted/30 p-4 md:p-6">
							<div className="flex items-center justify-between">
								<h2 className="text-lg font-semibold">Vendor Tycoon</h2>
								<Link to="/tycoon" className="text-xs text-muted-foreground hover:underline">Play</Link>
							</div>
							<p className="mt-2 text-sm">{formatGold(tycoon.allTimeGold)} gp earned all time</p>
							{tycoon.seasons.length > 0 && (
								<div className="mt-3 flex flex-wrap items-center gap-2">
									{tycoon.seasons.map((s) => {
										// The top three of a season get a badge of their own.
										const podium = TYCOON_PODIUM[s.rank]
										return podium ? (
											<div key={s.season} title={`#${s.rank} of ${s.season}, ${formatGold(s.gold)} gp`} className="flex items-center gap-2 rounded-lg border border-amber-500/50 bg-gradient-to-br from-amber-500/25 to-amber-500/5 px-3 py-1.5 text-sm font-semibold text-amber-700 shadow-sm dark:text-amber-300">
												<span className="text-lg leading-none">{podium.medal}</span>
												<span>{s.season} {podium.title}</span>
											</div>
										) : (
											<div key={s.season} title={`${formatGold(s.gold)} gp`} className="flex items-center gap-1.5 rounded-full border border-amber-500/20 bg-amber-500/10 px-2.5 py-1 text-xs font-medium text-amber-600 dark:text-amber-400">
												<span>{s.season}: {s.honour}</span>
												<span className="opacity-70">#{s.rank}</span>
											</div>
										)
									})}
								</div>
							)}
						</div>
					)}

					{/* Servers & Characters */}
					<div className="grid gap-6 sm:grid-cols-2">
						{/* Servers */}
						<div className="rounded-xl border bg-muted/30 p-4 md:p-6">
							<h2 className="text-lg font-semibold">Servers</h2>
							{user.shards.length === 0 ? (
								<p className="mt-2 text-sm text-muted-foreground">No servers listed.</p>
							) : (
								<div className="mt-3 space-y-2">
									{user.shards.map((shard) => (
										<Link
											key={shard.id}
											to={`/servers/${shard.shard?.server[0]?.slug}/shards/${shard.shard?.slug}/players`}
											className="flex items-center gap-2 rounded-lg bg-muted/50 px-3 py-2 text-sm transition-colors hover:bg-muted"
										>
											{shard.shard?.server[0]?.logo && (
												<img src={shard.shard.server[0].logo} alt="" className="h-5" />
											)}
											<span>{shard.shard?.server[0]?.name} - {shard.shard?.name}</span>
										</Link>
									))}
								</div>
							)}
							{isLoggedInUser && (
								<Link to="shards" className="mt-3 inline-block text-xs text-primary hover:underline">
									Manage shards
								</Link>
							)}
						</div>

						{/* Characters */}
						<div className="rounded-xl border bg-muted/30 p-4 md:p-6">
							<h2 className="text-lg font-semibold">Characters</h2>
							{characterNames.length === 0 ? (
								<p className="mt-2 text-sm text-muted-foreground">No characters listed.</p>
							) : (
								<div className="mt-3 space-y-1.5">
									{characterNames.map((char) => (
										<Link
											key={char.name}
											to="characters"
											className="flex items-center justify-between rounded-lg bg-muted/50 px-3 py-2 text-sm transition-colors hover:bg-muted"
										>
											<span>{char.name}</span>
											<span className="rounded-full bg-muted-foreground/10 px-2 py-0.5 text-xs text-muted-foreground">
												{char.count}
											</span>
										</Link>
									))}
								</div>
							)}
							{isLoggedInUser && (
								<Link to="characters" className="mt-3 inline-block text-xs text-primary hover:underline">
									Manage characters
								</Link>
							)}
						</div>
					</div>

					{/* Guilds */}
					{user.guilds.length > 0 && (
						<div className="rounded-xl border bg-muted/30 p-4 md:p-6">
							<h2 className="text-lg font-semibold">Guild History</h2>
							<div className="mt-3 space-y-2">
								{user.guilds.map((membership) => {
									const server = membership.guild.shard?.server[0]
									const shard = membership.guild.shard
									return (
										<div
											key={membership.id}
											className="rounded-lg bg-muted/50 px-3 py-2"
										>
											<div className="flex items-center gap-2 flex-wrap">
												{server && shard ? (
													<Link
														to={`/servers/${server.slug}/shards/${shard.slug}/guilds/${membership.guild.id}`}
														className="font-medium text-sm hover:underline"
													>
														{membership.guild.name}
													</Link>
												) : (
													<span className="font-medium text-sm">
														{membership.guild.name}
													</span>
												)}
												{membership.guild.abbreviation && (
													<span className="text-xs text-muted-foreground">
														[{membership.guild.abbreviation}]
													</span>
												)}
												{membership.role && (
													<span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
														{membership.role}
													</span>
												)}
											</div>
											<div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
												{server && shard && (
													<span>{server.name} · {shard.name}</span>
												)}
												{(membership.yearStart || membership.yearEnd) && (
													<span>
														{membership.yearStart ?? '?'} – {membership.yearEnd ?? 'present'}
													</span>
												)}
											</div>
											{membership.details && (
												<p className="mt-1 text-xs text-muted-foreground italic">
													"{membership.details}"
												</p>
											)}
										</div>
									)
								})}
							</div>
						</div>
					)}

					{/* Recent Content */}
					{(recentScripts.length > 0 || recentBuilds.length > 0 || recentGuides.length > 0) && (
						<div className="rounded-xl border bg-muted/30 p-4 md:p-6">
							<h2 className="text-lg font-semibold">Recent Content</h2>
							<div className="mt-3 space-y-2">
								{recentScripts.map((s) => (
									<Link
										key={s.slug}
										to={`/script/${s.slug}`}
										className="flex items-center gap-2 rounded-lg bg-muted/50 px-3 py-2 text-sm transition-colors hover:bg-muted"
									>
										<span className="shrink-0 rounded bg-blue-600/10 px-2 py-0.5 text-xs font-medium text-blue-600">Script</span>
										<span className="truncate">{s.title}</span>
									</Link>
								))}
								{recentBuilds.map((b) => (
									<Link
										key={b.slug}
										to={`/builds/${b.server.slug}/${b.slug}`}
										className="flex items-center gap-2 rounded-lg bg-muted/50 px-3 py-2 text-sm transition-colors hover:bg-muted"
									>
										<span className="shrink-0 rounded bg-purple-600/10 px-2 py-0.5 text-xs font-medium text-purple-600">Build</span>
										<span className="truncate">{b.name || 'Unnamed build'}</span>
									</Link>
								))}
								{recentGuides.map((g) => (
									<Link
										key={g.slug}
										to={`/guide/${g.slug}`}
										className="flex items-center gap-2 rounded-lg bg-muted/50 px-3 py-2 text-sm transition-colors hover:bg-muted"
									>
										<span className="shrink-0 rounded bg-green-600/10 px-2 py-0.5 text-xs font-medium text-green-600">Guide</span>
										<span className="truncate">{g.title}</span>
									</Link>
								))}
							</div>
							<Link to="activity" className="mt-3 inline-block text-xs text-primary hover:underline">
								View all activity
							</Link>
						</div>
					)}
				</div>

				{/* Right column - recent activity sidebar */}
				<div>
					<div className="sticky top-4 space-y-6">
						{/* Recent Comments */}
						<div className="rounded-xl border bg-muted/30 p-4 md:p-6">
							<div className="flex items-center justify-between">
								<h2 className="text-lg font-semibold">Recent Comments</h2>
								{counts.comments > 0 && (
									<Link to="comments" className="text-xs text-primary hover:underline">View all</Link>
								)}
							</div>
							{recentComments.length === 0 ? (
								<p className="mt-2 text-sm text-muted-foreground">No comments yet.</p>
							) : (
								<div className="mt-3 space-y-3">
									{recentComments.map((comment) => {
										let title = ''
										let link = ''
										if (comment.script) {
											title = comment.script.title
											link = `/script/${comment.script.slug}`
										} else if (comment.guide) {
											title = comment.guide.title
											link = `/guide/${comment.guide.slug}`
										} else if (comment.build) {
											title = comment.build.name
											link = `/builds/${comment.build.server.slug}/${comment.build.slug}`
										} else if (comment.newsPost) {
											title = comment.newsPost.title
											link = `/news/${comment.newsPost.slug}`
										} else if (comment.fanSite) {
											title = comment.fanSite.title
											link = `/fan-site/${comment.fanSite.slug}`
										} else if (comment.botScriptBuild) {
											title = comment.botScriptBuild.name
											link = `/script-builder/build/${comment.botScriptBuild.id}`
										}
										if (!link) return null

										return (
											<div key={comment.id} className="text-sm">
												<Link to={link} className="font-medium hover:underline line-clamp-1">{title}</Link>
												<p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">{comment.text}</p>
											</div>
										)
									})}
								</div>
							)}
						</div>

						{/* Quick Links */}
						<div className="rounded-xl border bg-muted/30 p-4 md:p-6">
							<h2 className="text-lg font-semibold">Quick Links</h2>
							<div className="mt-3 space-y-1.5">
								<Link to="activity" className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors hover:bg-muted">
									📋 <span>All Activity</span>
								</Link>
								<Link to="scripts" className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors hover:bg-muted">
									📜 <span>Scripts ({counts.scripts})</span>
								</Link>
								<Link to="builds" className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors hover:bg-muted">
									🛡️ <span>Builds ({counts.builds})</span>
								</Link>
								<Link to="reactions" className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors hover:bg-muted">
									⭐ <span>Reactions ({counts.reactions})</span>
								</Link>
								{isLoggedInUser && (
									<Link to="notes" className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors hover:bg-muted">
										📝 <span>Notes ({counts.notes})</span>
									</Link>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* External link warning modal */}
			{pendingExternalUrl && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
					<div className="w-full max-w-md rounded-xl border bg-background p-6 shadow-lg">
						<h3 className="text-lg font-semibold">Leaving UOAddicts</h3>
						<p className="mt-2 text-sm text-muted-foreground">
							You are about to visit an external website that is not associated with UOAddicts. We cannot guarantee the safety or content of external sites.
						</p>
						<p className="mt-3 break-all rounded-lg bg-muted px-3 py-2 text-sm font-mono">
							{pendingExternalUrl}
						</p>
						<p className="mt-3 text-xs text-muted-foreground">
							Proceed at your own risk.
						</p>
						<div className="mt-5 flex justify-end gap-3">
							<Button
								variant="outline"
								onClick={() => setPendingExternalUrl(null)}
							>
								Cancel
							</Button>
							<Button
								onClick={() => {
									window.open(pendingExternalUrl, '_blank', 'noopener,noreferrer')
									setPendingExternalUrl(null)
								}}
							>
								Continue
							</Button>
						</div>
					</div>
				</div>
			)}
		</div>
	)
}

function FollowButton({ targetUserId, initialFollowing, initialNotify }: { targetUserId: string; initialFollowing: boolean; initialNotify: boolean }) {
	const followFetcher = useFetcher()
	const notifyFetcher = useFetcher()

	const isFollowSubmitting = followFetcher.state !== 'idle'
	const optimisticFollowing = followFetcher.formData
		? followFetcher.formData.get('intent') === 'follow'
		: initialFollowing

	// Optimistic notify toggle: if submitting, flip the current state; otherwise use server response or initial
	const lastNotifyResult = (notifyFetcher.data as { notify?: boolean } | undefined)?.notify
	const currentNotify = lastNotifyResult ?? initialNotify
	const optimisticNotify = notifyFetcher.state !== 'idle' ? !currentNotify : currentNotify

	return (
		<div className="flex items-center gap-1">
			<followFetcher.Form method="POST" action="/resources/follow">
				<input type="hidden" name="targetUserId" value={targetUserId} />
				<input type="hidden" name="intent" value={optimisticFollowing ? 'unfollow' : 'follow'} />
				<button
					type="submit"
					disabled={isFollowSubmitting}
					className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
						optimisticFollowing
							? 'rounded-r-none border-r-0 bg-muted text-muted-foreground hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-950 dark:hover:text-red-400'
							: 'bg-blue-600 text-white hover:bg-blue-700'
					}`}
				>
					{optimisticFollowing ? 'Following' : 'Follow'}
				</button>
			</followFetcher.Form>
			{optimisticFollowing && (
				<notifyFetcher.Form method="POST" action="/resources/follow">
					<input type="hidden" name="targetUserId" value={targetUserId} />
					<input type="hidden" name="intent" value="toggleNotify" />
					<button
						type="submit"
						title={optimisticNotify ? 'Notifications on — click to mute' : 'Notifications off — click to enable'}
						className="rounded-full rounded-l-none bg-muted px-2.5 py-1.5 text-sm transition-colors hover:bg-muted-foreground/10"
					>
						{optimisticNotify ? '🔔' : '🔕'}
					</button>
				</notifyFetcher.Form>
			)}
		</div>
	)
}

export const meta: MetaFunction<typeof loader> = ({ data, params }) => {
	const displayName = data?.user.name ?? params.username
	return [
		{ title: `${displayName} | ${brandName}` },
		{
			name: 'description',
			content: `Profile of ${displayName} on ${brandName}`,
		},
	]
}

export function ErrorBoundary() {
	return (
		<GeneralErrorBoundary
			statusHandlers={{
				404: ({ params }) => (
					<p>No user with the username "{params.username}" exists</p>
				),
			}}
		/>
	)
}
